import React, { useState, useEffect, useContext, createContext } from "react";
import PropTypes from "prop-types";
import cookie from "js-cookie";
import queryString from "query-string";
import * as firebase from "firebase/app";
import "firebase/auth";
import cookies from "next-cookies";
import axios from "axios";
import production from "../.firebase/production.json";
import development from "../.firebase/development.json";
import { getBackendUrl } from "./env";
import { useRouter } from "next/router";

if (!firebase.apps.length) {
  if (process.env.NEXT_PUBLIC_FIREBASE_ENVIRONMENT === "production") {
    firebase.initializeApp(production);
  } else if (process.env.NEXT_PUBLIC_FIREBASE_ENVIRONMENT === "development") {
    firebase.initializeApp(development);
  }
}

// Helper function to get firebaseToken from request headers.
export function getTokenFromCtx(ctx) {
  let firebaseToken = null;
  if (ctx && ctx.req) {
    // Get cookies server side
    firebaseToken = cookies(ctx).orchidAuth;
  } else {
    // Get cookies client side
    firebaseToken = cookie.get("orchidAuth");
  }
  return firebaseToken;
}

// Redirects if the user isn't confirmed
function handleUnverifiedEmail(ctx, nextUrl = null) {
  // ctx.req might be undefined if called from the client
  if (!nextUrl) {
    if (ctx && ctx.req && ctx.req.url) {
      nextUrl = ctx.req.url;
    } else {
      nextUrl = window.location.pathname;
    }
  }
  if (ctx && ctx.res && ctx.res.writeHeader) {
    ctx.res.writeHeader(302, {
      Location: `/verify-email?next=${encodeURI(nextUrl)}`,
    });
    ctx.res.end();
  } else {
    window.location.replace(nextUrl);
  }
}

// Redirects if the request isn't authorized for the requested
// resource.
function handleUnauthorized(ctx, nextUrl = null) {
  // ctx.req might be undefined if called from the client
  if (!nextUrl) {
    if (ctx && ctx.req && ctx.req.url) {
      nextUrl = ctx.req.url;
    } else {
      nextUrl = window.location.pathname;
    }
  }
  if (ctx && ctx.res && ctx.res.writeHeader) {
    ctx.res.writeHeader(302, {
      Location: `/signin?next=${encodeURI(nextUrl)}`,
    });
    ctx.res.end();
  } else {
    window.location.replace(nextUrl);
  }
}

export async function postUnauthenticatedRoute(uri, _, data) {
  return makeAuthenticatedRoute(uri, null, {
    method: "post",
    data,
    skipAuth: true,
  });
}

export async function postAuthenticatedRoute(uri, ctx, data) {
  return makeAuthenticatedRoute(uri, ctx, { method: "post", data });
}

export function getAuthenticatedRoute(uri, ctx = {}) {
  return makeAuthenticatedRoute(uri, ctx, { method: "get" });
}

async function makeAuthenticatedRoute(uri, ctx, opts) {
  const {
    // If skipAuth is true, this function makes an unauthenticated request
    skipAuth,
    ...axiosOpts
  } = opts;
  const url = getBackendUrl(uri);
  if (process.env.DEBUG) {
    console.log(`Requesting ${url}`);
  }

  const firebaseToken = getTokenFromCtx(ctx);

  if (!firebaseToken && !skipAuth) {
    return handleUnauthorized(ctx);
  }

  let result;
  try {
    const response = await axios({
      method: "get",
      url: getBackendUrl(uri),
      ...(!skipAuth && {
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
      }),
      ...axiosOpts,
    });
    result = response.data;
  } catch (error) {
    // Handle our 401s by redirecting
    if (error.response && error.response.status === 401) {
      return handleUnauthorized(ctx);
    }
    if (error.response && error.response.status === 403) {
      return handleUnverifiedEmail(ctx);
    }

    console.error(`Error from ${url}`);
    console.error(error);
    // Re-throw so we can use default Next error-handling
    // toJSON just strips all of the extra Axios methods so we get cleaner
    // logs.
    // TODO: These will be quite spammy. Figure out the right properties to extract and
    // only log those.
    console.log(error.request);
    console.log(error.response);
    throw error;
  }

  if (process.env.DEBUG) {
    console.log(`Response from ${url}: ${JSON.stringify(result, null, 2)}`);
  }

  // If a user doesn't have an account, we display an
  // error in the dashboard. This behavior is weird --
  // the user ends up being redirected to the login
  // page (/signin?next=/get_account) with no explanation

  // if (!result || Object.keys(result).length === 0) {
  //   if (process.env.DEBUG) {
  //     console.log("Not signed in!");
  //   }

  //   return handleUnauthorized(ctx, uri);
  // }

  return result;
}

// Helper function to get user from request headers. Should be called from getInitialProps.
// Even though this redirects to /signin for when this is called on the server side,
// you should still check if the returned user is empty and redirect to signin
// on the client side using useEffect or some other function during render time.
export async function getUserFromCtx(ctx) {
  // TODO: Staging vs prod environments?
  return getAuthenticatedRoute("/get_logged_in_user", ctx);
}

const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

ProvideAuth.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  return useContext(authContext);
};

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const router = useRouter();

  const setUserAndCookie = async (rawUser, token = null) => {
    // If token is not null, use that one instead of whatever token
    // is in rawUser
    // See https://firebase.google.com/docs/reference/js/firebase.User#getidtoken
    //
    // Previously this was `rawUser.xa`, but that was failing for me. See this ticket:
    // https://github.com/vercel/next.js/discussions/18036#discussioncomment-109656
    // If this has any impact, we can revert to `rawUser.xa || rawUser.ya`, but I believe this is
    // the canonical solution.
    let userToken = await rawUser.getIdToken();

    if (token) {
      userToken = token;
    }

    setUser(rawUser);

    cookie.set("orchidAuth", userToken, {
      expires: 1,
    });
  };

  const removeUserCookie = async () => {
    setUser(false);
    cookie.remove("orchidAuth");
  };

  const signin = (email, password) => {
    email = email.trim();
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then((response) => {
        setUserAndCookie(response.user);
        return response.user;
      });
  };

  const signinWithGoogle = () => {
    return firebase
      .auth()
      .signInWithPopup(new firebase.auth.GoogleAuthProvider())
      .then((response) => {
        setUserAndCookie(response.user);
        return response.user;
      });
  };

  const signup = (email, password) => {
    email = email.trim();
    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((response) => {
        setUserAndCookie(response.user);
        return response.user;
      });
  };

  const signout = () => {
    return firebase
      .auth()
      .signOut()
      .then(() => {
        removeUserCookie();
        router.push("/signin");
      });
  };

  const sendPasswordResetEmail = (email) => {
    email = email.trim();
    // This request doesn't use authenticated backend request
    return axios({
      method: "get",
      url: getBackendUrl(`/reset_password?email=${email}`),
    });
  };

  const confirmPasswordReset = (password, code) => {
    const resetCode = code || getFromQueryString("oobCode");
    return firebase
      .auth()
      .confirmPasswordReset(resetCode, password)
      .then(() => {
        return true;
      });
  };

  const sendEmailConfirmation = (email) => {
    email = email.trim();
    // This request doesn't use authenticated backend request
    return axios({
      method: "get",
      url: getBackendUrl(`/verify_email?email=${email}`),
    });
  };

  const confirmEmail = (code) => {
    const resetCode = code || getFromQueryString("oobCode");
    return firebase
      .auth()
      .applyActionCode(resetCode)
      .then(() => {
        return true;
      });
  };

  const refreshToken = () => {
    console.log("refreshing token");
    firebase.auth().currentUser.reload();
  };

  // https://firebase.google.com/docs/reference/js/firebase.User#returns-promisestring
  // Returns Promise<string>
  const refreshBackendToken = async () => {
    const maybeNewToken = await firebase.auth().currentUser.getIdToken(true);
    // maybeNewToken is used instead of whatever token is in the currentUser
    // it SHOULD be consistent but firebase can have weird edge cases
    setUserAndCookie(firebase.auth().currentUser, maybeNewToken);
  };

  const getFirebaseUser = () => {
    try {
      return firebase.auth().currentUser;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
      } else {
        removeUserCookie();
      }
    });

    return () => unsubscribe();
  }, []);

  return {
    userId: user && user.uid,
    signin,
    signinWithGoogle,
    signup,
    signout,
    sendPasswordResetEmail,
    confirmPasswordReset,
    sendEmailConfirmation,
    confirmEmail,
    refreshToken,
    refreshBackendToken,
    getFirebaseUser,
  };
}

const getFromQueryString = (key) => {
  return queryString.parse(window.location.search)[key];
};
